/**
 * Selected Intelligence Insights analysis context (shared with Ask Lens Redux).
 */

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectedWorkstreamIds } from './aiWardenScope';
import { contextModeLabel } from './askAiChatMeta';
import { getUnionCustomWorkstreamIds } from './lensIntelligenceCustomContext';

const CONTEXT_CUSTOM = 'custom';
const CONTEXT_WORKSTREAMS = 'select_workstreams';

export function chooseInsightsContextLabel(mode, selectedWorkstreams) {
    if (mode === CONTEXT_CUSTOM) return 'Custom Sources';
    if (mode === CONTEXT_WORKSTREAMS) {
        const n = Array.isArray(selectedWorkstreams) ? selectedWorkstreams.length : 0;
        if (n <= 0) return 'Workstreams';
        if (n === 1) return 'Selected workstream';
        return `${n} workstreams`;
    }
    return contextModeLabel('entire_project');
}

export function buildInsightsAnalysisContext(
    contextMode,
    selectedWorkstreams,
    effectiveWorkstreamIds,
) {
    const workstreamIds = (effectiveWorkstreamIds || [])
        .map(id => Number(id))
        .filter(Number.isFinite);
    if (contextMode === CONTEXT_WORKSTREAMS) {
        return {
            contextType: 'workstreams',
            contextLabel: chooseInsightsContextLabel(contextMode, selectedWorkstreams),
            workstreamIds,
        };
    }
    if (contextMode === CONTEXT_CUSTOM) {
        return {
            contextType: 'custom_sources',
            contextLabel: chooseInsightsContextLabel(contextMode, selectedWorkstreams),
            workstreamIds,
        };
    }
    return {
        contextType: 'entire_project',
        contextLabel: chooseInsightsContextLabel(contextMode, selectedWorkstreams),
        workstreamIds: undefined,
    };
}

export function insightsContextQueryString({
    contextType,
    contextLabel,
    workstreamIds,
    analysisId,
} = {}) {
    if (!contextType) return '';
    const params = new URLSearchParams();
    params.set('context_type', contextType);
    if (contextLabel) params.set('context_label', contextLabel);
    if (contextType !== 'entire_project' && Array.isArray(workstreamIds)) {
        workstreamIds.forEach(id => params.append('workstream_ids', String(id)));
    }
    if (analysisId) params.set('analysis_id', String(analysisId));
    const qs = params.toString();
    return qs ? `?${qs}` : '';
}

export function withInsightsContextQuery(url, context, options = {}) {
    const extra = insightsContextQueryString({
        ...context,
        analysisId: options.analysisId || context?.analysisId || null,
    });
    if (!extra) return url;
    const [base, existing] = String(url || '').split('?');
    if (!existing) return `${base}${extra}`;
    return `${base}?${existing}&${extra.slice(1)}`;
}

export function insightsContextKey(context, analysisId = null) {
    const type = context?.contextType || 'entire_project';
    const ids = (context?.workstreamIds || []).join(',');
    const versionKey = analysisId ? `:analysis:${analysisId}` : '';
    return `${type}:${ids}${versionKey}`;
}

export function useInsightsAnalysisContext() {
    const contextMode = useSelector(state => state.intelligence?.askContextMode);
    const selectedWorkstreams = useSelector(state => state.intelligence?.askSelectedWorkstreams);
    const customContext = useSelector(state => state.intelligence?.askCustomContext);
    const workstreams = useSelector(state => state.dataroom?.workstreams);

    return useMemo(() => {
        const allIds = (workstreams || []).map(w => String(w.id)).filter(Boolean);
        const effective =
            contextMode === CONTEXT_CUSTOM
                ? getUnionCustomWorkstreamIds(customContext, allIds)
                : selectedWorkstreamIds(selectedWorkstreams, allIds);
        return buildInsightsAnalysisContext(contextMode, selectedWorkstreams, effective);
    }, [contextMode, selectedWorkstreams, customContext, workstreams]);
}
